import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    input,
    output,
    ViewEncapsulation,
    forwardRef,
    signal,
    computed
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { RadioButtonModule } from 'primeng/radiobutton';

@Component({
    selector: 'fcp-radiobutton',
    standalone: true,
    imports: [CommonModule, RadioButtonModule, FormsModule],
    styleUrl: './fcp-radiobutton.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FcpRadiobuttonComponent),
            multi: true
        }
    ],
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `
        <div [class]="wrapperClasses()">
            <p-radiobutton
                [value]="value()"
                [ngModel]="model()"
                [ngModelOptions]="{ standalone: true }"
                [name]="name()"
                [disabled]="isDisabled()"
                [variant]="radioButtonVariant()"
                [size]="size()"
                [tabindex]="tabindex()"
                [inputId]="resolvedInputId()"
                [ariaLabelledBy]="ariaLabelledBy()"
                [ariaLabel]="ariaLabel()"
                [styleClass]="styleClass()"
                [autofocus]="autofocus()"
                [binary]="binary()"
                (ngModelChange)="handleChange($event)"
                (onClick)="onClick.emit($event)"
                (onFocus)="onFocus.emit($event)"
                (onBlur)="handleBlur($event)"
            />
            <div [class]="labelsWrapperClasses()">
                @if (label()) {
                    <label [class]="primaryLabelClasses()" [for]="resolvedInputId()">{{ label() }}</label>
                    @if (additionalLabel()) {
                        <label [class]="additionalLabelClasses()" [for]="resolvedInputId()">{{
                            additionalLabel()
                        }}</label>
                    }
                }
            </div>
        </div>
    `
})
export class FcpRadiobuttonComponent implements ControlValueAccessor {
    private static nextGeneratedInputId = 0;

    e2e = input<string | null>(null);
    value = input<unknown>();
    name = input<string | undefined>();
    disabled = input<boolean>(false);
    radioButtonVariant = input<RadioButtonVariant | undefined>();
    size = input<RadioButtonSize>(RadioButtonSize.Normal);
    tabindex = input<number | undefined>();
    inputId = input<string | undefined>();
    ariaLabelledBy = input<string | undefined>();
    ariaLabel = input<string | undefined>();
    styleClass = input<string | undefined>();
    autofocus = input<boolean>(false);
    binary = input<boolean>(false);
    label = input<string>();
    additionalLabel = input<string>();

    onClick = output<RadioButtonClickEvent>();
    onFocus = output<FocusEvent>();
    onBlur = output<FocusEvent>();

    private _value = signal<unknown>(null);
    private _onChange = signal<(value: unknown) => void>(() => {});
    private _onTouched = signal<() => void>(() => {});
    private _formDisabled = signal<boolean>(false);
    private readonly generatedInputId = `fcp-radiobutton-${FcpRadiobuttonComponent.nextGeneratedInputId++}`;

    model = computed(() => this._value());
    isDisabled = computed(() => this.disabled() || this._formDisabled());
    resolvedInputId = computed(() => this.inputId() || this.generatedInputId);
    primaryLabelClasses = computed(
        () => 'fcp-radiobutton-wrapper-labels-primary' + (this.isDisabled() ? ' disabled' : '')
    );
    additionalLabelClasses = computed(
        () => 'fcp-radiobutton-wrapper-labels-additional' + (this.isDisabled() ? ' disabled' : '')
    );
    labelsWrapperClasses = computed(
        () =>
            'fcp-radiobutton-wrapper-labels' +
            (!this.additionalLabel() ? ' fcp-radiobutton-wrapper-labels--single' : '')
    );
    wrapperClasses = computed(
        () =>
            'fcp-radiobutton-wrapper' + (this.size() === RadioButtonSize.Small ? ' fcp-radiobutton-wrapper--small' : '')
    );

    writeValue(value: unknown): void {
        this._value.set(value ?? null);
    }

    registerOnChange(fn: (value: unknown) => void): void {
        this._onChange.set(fn);
    }

    registerOnTouched(fn: () => void): void {
        this._onTouched.set(fn);
    }

    setDisabledState(isDisabled: boolean): void {
        this._formDisabled.set(isDisabled);
    }

    handleChange(value: unknown): void {
        this._value.set(value);
        this._onChange()(value);
        this.deferTouched();
    }

    handleBlur(event: FocusEvent): void {
        this.onBlur.emit(event);
        this.deferTouched();
    }

    /** Defer touched so value updates can run before `ng-touched` flips. */
    private deferTouched(): void {
        queueMicrotask(() => this._onTouched()());
    }
}

export enum RadioButtonVariant {
    Outlined = 'outlined',
    Filled = 'filled'
}

export enum RadioButtonSize {
    Small = 'small',
    Normal = null
}

export interface RadioButtonClickEvent {
    originalEvent: Event;
    value: unknown;
}
